import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Button } from '@/shared/ui/button';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { lireCodePostal, ressembleAUnCodePostal } from '@/shared/api/geo';
import RecherchePdl from '@/shared/components/RecherchePdl';
import RetrouverParPdl from '@/shared/components/RetrouverParPdl';
import RechercheAdresse from '@/shared/components/RechercheAdresse';

// Etape 1 du wizard installateur : informations client + logement (présentationnel).
export default function StepClientInfo({ t = (c, s) => s || c, client, up, pdlError, menuData, step1Valid, setStep }) {
  // L'adresse email est obligatoire depuis le 03/09, et le serveur refuse la signature
  // sans elle (`backend/app/core/email_saisie.py`). Le controle est refait ICI pour deux
  // raisons que `step1Valid` ne couvre pas :
  //   - une valeur faite d'espaces est « vraie » en JavaScript et passerait l'etape,
  //     alors que le serveur la lit `.strip()` et la refuse ;
  //   - un bouton grise ne dit pas CE QUI manque. Le poseur est chez le client, il
  //     cherche, et rien a l'ecran ne lui repond.
  const emailManquant = !(client.email || '').trim();
  return (
    <div className="space-y-4 animate-fade-in" data-testid="step-client">
      <h2 className="text-lg font-semibold text-neutral-900">{t('step1_title', "Informations client")}</h2>
      <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] text-neutral-500">Civilite *</Label>
            <Select value={client.civilite} onValueChange={v => up('civilite', v)}>
              <SelectTrigger className="h-11 text-[14px]" data-testid="inst-civilite" aria-label={t('lbl_civilite', 'Civilité')}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_client_prenom', 'Prénom client')} *</Label>
            <Input className="h-11 text-[14px]" value={client.prenom} onChange={e => up('prenom', e.target.value)} data-testid="inst-prenom" />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_client_nom', 'Nom client')} *</Label>
            <Input className="h-11 text-[14px]" value={client.nom} onChange={e => up('nom', e.target.value)} data-testid="inst-nom" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] text-neutral-500">{t('field_adresse', 'Adresse du chantier')} *</Label>
          {/* Le champ PROPOSE, il n'impose rien : c'est un Input ordinaire avec une
              liste posee a cote. La BAN muette, le poseur tape comme avant.
              Un choix remplit adresse, code postal et ville d'un coup, puis fait
              resoudre la region par le SERVEUR -- exactement le geste du champ code
              postal ci-dessous, pour que les deux chemins ne divergent jamais. */}
          <RechercheAdresse
            valeur={client.adresse}
            onChange={v => up('adresse', v)}
            onChoisir={p => {
              up('adresse', p.adresse);
              up('code_postal', p.code_postal);
              up('ville', p.ville);
              if (!ressembleAUnCodePostal(p.code_postal)) return;
              lireCodePostal(p.code_postal)
                .then(geo => { if (geo.region) up('region', geo.region); })
                .catch(() => { /* code postal inconnu : la liste des regions reste au poseur */ });
            }}
            classeChamp="h-11 text-[14px]"
            testid="inst-adresse" />
        </div>
        <div>
          {/* Ce qu'Enedis appelle batiment, escalier et etage, et qu'il rend quand il
              le connait. Facultatif : sur une maison il n'y a rien a mettre. */}
          <Label className="text-[11px] text-neutral-500">{t('field_complement_adresse', "Complement d'adresse")}</Label>
          <Input className="h-11 text-[14px]" value={client.complement_adresse || ''}
            onChange={e => up('complement_adresse', e.target.value)}
            placeholder="Batiment, escalier, etage"
            data-testid="inst-complement-adresse" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_code_postal', "Code postal")} *</Label>
            <Input className="h-11 text-[14px]" value={client.code_postal} onChange={e => {
              const cp = e.target.value;
              up('code_postal', cp);
              // La region vient du SERVEUR, plus d'une table cote navigateur.
              //
              // `getRegionFromCP` decoupait sur les deux premiers chiffres du code
              // postal. C'est une approximation CONNUE POUR FAUSSE sur 01590, 97133
              // (Saint-Barthelemy) et 97150 (Saint-Martin), incapable d'exprimer les
              // cinq codes postaux a cheval sur deux regions, et c'etait la troisieme
              // liste de regions du produit. Deux referentiels finissent toujours par
              // diverger ; celui qui se trompe le fait sans erreur visible.
              //
              // On ne crie pas pendant la frappe : un code postal inconnu ne remplit
              // rien, et le serveur refusera l'enregistrement en disant pourquoi.
              if (!ressembleAUnCodePostal(cp)) return;
              lireCodePostal(cp)
                .then(geo => { if (geo.region) up('region', geo.region); })
                .catch(() => { /* code postal inconnu : on laisse la liste au poseur */ });
            }} data-testid="inst-cp" />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_ville', "Ville")} *</Label>
            <Input className="h-11 text-[14px]" value={client.ville} onChange={e => up('ville', e.target.value)} data-testid="inst-ville" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] text-neutral-500">Region *</Label>
          <Select value={client.region || ''} onValueChange={v => up('region', v)}>
            <SelectTrigger className="h-11 text-[14px]" data-testid="inst-region" aria-label={t('lbl_region', 'Région')}><SelectValue placeholder={t('lbl_region_placeholder', "Sélectionner une région")} /></SelectTrigger>
            <SelectContent>
              {(menuData.regions || []).map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[11px] text-neutral-500">
              {t('field_pdl_prm', 'N° PRM / PDL')} *{' '}
              <span className="text-neutral-400">({t('lbl_pdl_prm_hint', '14 chiffres')})</span>
            </Label>
            <a href="https://www.enedis.fr/faq/compteur-linky/ou-trouver-le-numero-point-de-livraison-pdl-du-compteur-linky" target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-neutral-400 flex items-center gap-0.5 hover:underline">
              {t('lbl_find_pdl', "Trouver son PDL")} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>
          <Input
            className={`h-9 ${pdlError ? 'border-red-400' : ''}`}
            value={client.pdl_prm}
            onChange={e => up('pdl_prm', e.target.value.replace(/\D/g, '').slice(0, 14))}
            placeholder="Ex: 09123456789012"
            data-testid="inst-pdl"
          />
          {/* La recherche vient APRES le champ, pas a la place : le poseur qui a le
              numero sous les yeux le tape, celui qui ne l'a pas le cherche. Une
              recherche qui ne trouve pas ne doit jamais empecher de saisir. */}
          <div className="mt-1.5">
            <RecherchePdl client={client} valeurActuelle={client.pdl_prm}
              onTrouve={pdl => up('pdl_prm', pdl)}
              onCorrigerChamps={champs => Object.entries(champs).forEach(([c, v]) => up(c, v))}
              testid="inst-pdl-recherche" />
          </div>
          {/* LE SENS RETOUR, pour le cas exactement inverse : le poseur a le numero sous
              les yeux, releve sur le compteur, et c'est l'ADRESSE qu'il veut confronter.
              Les deux gestes cohabitent parce qu'ils ne servent pas le meme moment --
              chercher quand on n'a pas le numero, verifier quand on l'a. */}
          <div className="mt-1.5">
            <RetrouverParPdl client={client} authentifie
              onCorrigerChamps={champs => Object.entries(champs).forEach(([c, v]) => up(c, v))}
              testid="inst-pdl-retrouver" />
          </div>
          {pdlError && <p className="text-[11px] text-red-500 mt-1" data-testid="pdl-error">{pdlError}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_client_email', 'Email client')} *</Label>
            <Input className={`h-11 text-[14px] ${emailManquant ? 'border-red-400' : ''}`} type="email" value={client.email} onChange={e => up('email', e.target.value)} data-testid="inst-email" />
            {emailManquant && (
              /* « Obligatoire » SEUL, et traduit. David (Loom du 13/10) : « juste
                 "obligatoire" » ; Boubacar l'a tranche le 27/10 (lot L5). L'ancienne
                 explication (attestation, mandat) etait de toute facon fausse depuis
                 que ni l'un ni l'autre ne part plus au client (03/09, 07/09). Le
                 telephone n'a jamais eu de texte equivalent : rien a retirer dessous. */
              <p className="text-[11px] text-red-500 mt-1" data-testid="inst-email-obligatoire">
                {t('field_obligatoire', 'Obligatoire')}
              </p>
            )}
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">{t('field_client_telephone', 'Téléphone client')} *</Label>
            <Input className="h-11 text-[14px]" value={client.telephone} onChange={e => up('telephone', e.target.value)} data-testid="inst-tel" />
          </div>
        </div>
      </div>

      {/* Informations logement */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3" data-testid="logement-section">
        <p className="text-[12px] font-medium text-neutral-700">{t('section_logement', "Informations logement")}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-neutral-500">{t('lbl_type_logement', "Type de logement")} *</Label>
            <Select value={client.type_logement} onValueChange={v => up('type_logement', v)}>
              <SelectTrigger className="h-11 text-[14px]" data-testid="inst-type-logement"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Maison">Maison</SelectItem>
                <SelectItem value="Appartement">Appartement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">{t('lbl_surface', "Surface (m²)")}</Label>
            <Input className="h-11 text-[14px]" type="number" min="0" value={client.surface_m2} onChange={e => up('surface_m2', e.target.value)} data-testid="inst-surface" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] text-neutral-500">{t('lbl_mode_chauffage', "Mode de chauffage")} *</Label>
          <Select value={client.mode_chauffage} onValueChange={v => up('mode_chauffage', v)}>
            <SelectTrigger className="h-11 text-[14px]" data-testid="inst-mode-chauffage"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Électrique">Électrique</SelectItem>
              <SelectItem value="Gaz">Gaz</SelectItem>
              <SelectItem value="Fioul">Fioul</SelectItem>
              <SelectItem value="PAC Air/Air">PAC Air/Air</SelectItem>
              <SelectItem value="PAC Air/Eau">PAC Air/Eau</SelectItem>
              <SelectItem value="Collectif">Collectif</SelectItem>
              <SelectItem value="Autre">Autre</SelectItem>
              <SelectItem value="Inconnu">Inconnu</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button className="w-full h-12 text-[15px]" disabled={!step1Valid || emailManquant} onClick={() => setStep(2)} data-testid="step1-next-btn">
        {t('btn_next', "Suivant")} <ArrowRight className="w-5 h-5 ml-1.5" />
      </Button>
    </div>
  );
}
